import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useExcerpt } from 'hooks/excerpts/queries';
import { useExcerptWithStartAndEnd } from 'hooks/excerpts/useExcerptWithStartAndEnd';

const useSelectedExcerpt = (initialExcerptId) => {
  const [excerptId, setExcerptId] = useState(initialExcerptId);
  const [rawSelect, setRawSelect] = useState(null);
  const [selectedExcerptVisible, setSelectedExcerptVisible] = useState(true);

  const { data: excerpt } = useExcerpt(excerptId);

  // Use useMemo to transform select data
  const select = useMemo(() => {
    // First priority: if we have an excerpt, use that
    if (excerptId && excerpt) {
      return {
        start: parseInt(excerpt.start),
        end: parseInt(excerpt.end),
        codeableId: parseInt(excerpt.transcript_id),
        codeableType: excerpt.codeable_type,
      };
    }
    
    // Second priority: use rawSelect if available
    if (rawSelect) {
      return {
        start: parseInt(rawSelect.start),
        end: parseInt(rawSelect.end),
        codeableId: parseInt(rawSelect.codeableId),
        codeableType: rawSelect.codeableType,
        metadata: rawSelect.metadata,
      };
    }
    
    return null;
  }, [excerptId, excerpt, rawSelect]);

  // Get the excerpt that matches the current selection
  const matchingExcerpt = useExcerptWithStartAndEnd(
    rawSelect?.start,
    rawSelect?.end, 
    rawSelect?.codeableId,
    rawSelect?.codeableType
  );

  // Update excerptId when we find a matching excerpt
  useEffect(() => {
    if (matchingExcerpt?.id && rawSelect) {
      setExcerptId(matchingExcerpt.id);
      setRawSelect(null);
    }
  }, [matchingExcerpt, rawSelect]);

  const onSelectText = useCallback((start, end, codeableId, codeableType, metadata) => {
    const startNum = parseInt(start);
    const endNum = parseInt(end);

    // Guard against invalid ranges. In Firefox, double-clicking a paragraph can
    // leave the selection boundary on a non-.codeSpan node (e.g. the <p>), which
    // makes getLoc return NaN. A NaN selection matches every paragraph (all NaN
    // comparisons are false, so the overlap check inverts to true) and blanks
    // their text. Ignore it.
    if (!Number.isFinite(startNum) || !Number.isFinite(endNum)) {
      return;
    }

    // First, clear any existing excerpt ID and set the raw selection
    setExcerptId(null);
    setRawSelect({ start: startNum, end: endNum, codeableId: parseInt(codeableId), codeableType, metadata });
    setSelectedExcerptVisible(true);
  }, []);

  const onNewExcerptId = useCallback((excerpt_id) => {
    if (excerpt_id) {
      // Just set the excerpt ID - the useMemo will handle the rest
      setExcerptId(excerpt_id.toString());
      setRawSelect(null); // Clear raw select since we're using an excerpt
    } else {
      setExcerptId(null);
      setRawSelect(null);
    }
    setSelectedExcerptVisible(true);
  }, []);

  const onDeselect = useCallback(() => {
    setExcerptId(null);
    setRawSelect(null);
    setSelectedExcerptVisible(true);
  }, []);

  return {
    excerptId,
    excerpt,
    select, // Now computed via useMemo
    onNewExcerptId,
    onDeselect,
    onSelectText,
    selectedExcerptVisible,
    setSelectedExcerptVisible,
  };
};

export default useSelectedExcerpt;